/* eslint-disable default-param-last */
// 数字处理 https://mikemcl.github.io/bignumber.js/#rounding-mode
import { formatNumber } from '@web3/base-utils';
import BN from 'bignumber.js';
import { curryRight } from 'lodash-es';

BN.config({
  DECIMAL_PLACES: 20, // 小数位数
  EXPONENTIAL_AT: 20, // 科学标识法位数
});

// 处理返回结果
function handleReturn(result): any {
  const rs: any = result.toFixed ? result.toFixed() : result;

  return /[a-z]/i.test(rs) ? '0' : rs;
}

/**
 *
 * 生成随机数
 *
 * @param {number} num 随机小数位数
 *
 * random(3) // '0.105'
 * random(1) // '0.1'
 */
function random(num: number) {
  return handleReturn(BN.random(num));
}

/**
 * 累计和
 *
 * @param {...str} str 需要累加的字符串
 *
 * sum(['1', '2', '3']) // '6'
 * sum([null, '1']) // '0'
 * sum(['sdaf', '1']) // '0'
 */
function sum(str: string[]) {
  return handleReturn(BN.sum(...str));
}

/**
 * 绝对值
 *
 * @param {str} str 字符串
 *
 * abs('-112') // '112'
 * abs('dsaf') // '0'
 */
function abs(str: string) {
  return handleReturn(BN(`${str}`).abs());
}

/**
 * 加法
 *
 * @param {string} s1 第一个值
 * @param {string} s2 第二个值
 *
 * plus('1', '9') // '10'
 * plus('dsaf', 1) // '0'
 */
function plus(s1, s2) {
  return handleReturn(BN(`${s1}`).plus(`${s2}`));
}

/**
 * 减法
 *
 * @param {string} s1 第一个值
 * @param {string} s2 第二个值
 *
 * minus('10', '5') // '5'
 * minus('dsaf', 1) // '0'
 */
function minus(s1, s2) {
  return handleReturn(BN(`${s1}`).minus(`${s2}`));
}

/**
 * 乘法
 *
 * @param {string} s1 第一个值
 * @param {string} s2 第二个值
 *
 * times('1', '9') // '9'
 * times('dsaf', 1) // '0'
 */
function times(s1, s2) {
  return handleReturn(BN(`${s1}`).times(`${s2}`));
}

/**
 * 除法
 *
 * @param {string} s1 第一个值
 * @param {string} s2 第二个值
 *
 * div('8', '1') // '8'
 * div('10', '3') // '3.33333333333333333333333333333333333333333333333333333333333333333333333'
 * div('dsaf', 1) // '0'
 */
function div(s1, s2) {
  if (`${s2}` === '0') return '0';
  return handleReturn(BN(`${s1}`).div(`${s2}`));
}

/**
 * 整数除法
 *
 * @param {string} s1 第一个值
 * @param {string} s2 第二个值
 *
 * idiv('10', '3') // '3'
 * idiv('dsaf', 1) // '0'
 */
function idiv(s1, s2) {
  if (`${s2}` === '0') return '0';
  return handleReturn(BN(`${s1}`).idiv(`${s2}`));
}

/**
 * 大于
 *
 * @param {string} s1 第一个值
 * @param {string} s2 第二个值
 *
 * gt('4', '2') // true
 * gt('3', '3') // false
 * gt('dsaf', 1) // false
 */
function gt(s1, s2) {
  return BN(`${s1}`).gt(`${s2}`);
}

/**
 * 大于等于
 *
 * @param {string} s1 第一个值
 * @param {string} s2 第二个值
 *
 * gte('10', '10') // true
 * gte('10', '3') // false
 * gte('dsaf', 1) // false
 */
function gte(s1, s2) {
  return BN(`${s1}`).gte(`${s2}`);
}

/**
 * 小于
 *
 * @param {string} s1 第一个值
 * @param {string} s2 第二个值
 *
 * lt('2', '3') // true
 * lt('4', '2') // false
 * lt('dsaf', 1) // false
 */
function lt(s1, s2) {
  return BN(`${s1}`).lt(`${s2}`);
}

/**
 * 小于等于
 *
 * @param {string} s1 第一个值
 * @param {string} s2 第二个值
 *
 * lte('2', '2') // true
 * lte('4', '2') // false
 * lte('dsaf', 1) // false
 */
function lte(s1, s2) {
  return BN(`${s1}`).lte(`${s2}`);
}

/**
 * 等于
 *
 * @param {string} s1 第一个值
 * @param {string} s2 第二个值
 *
 * eq('10', '10') // true
 * eq('10', '3') // false
 * eq('dsaf', 1) // false
 */
function eq(s1, s2) {
  return BN(`${s1}`).eq(`${s2}`);
}

/**
 * 整体精度精度
 *
 * @param {string} s1 需要取精度的字符串
 * @param {number} d 几位精度
 * @param {string} rm 四舍五入方式 默认向上取证，1向下取整
 *
 * sd('125.125', 2) // '130'
 * sd('125.125', 2, 1) // '120'
 * sd('sdfsd', 2, 1) // '0'
 */
function sd(s1, d, rm) {
  if (!d) throw new Error('精度为必填');
  return handleReturn(BN(`${s1}`).sd(d, rm));
}

/**
 * 不带千分位保留几位小数 默认为向下取整
 *
 * @param {string} s1 需要格式化的字符串
 * @param {number} dp 保留几位小数 默认保留所有小数
 * @param {number} [rm=1] 最后一位保留方式，默认为1向下取整
 *
 * toFixed('123.555') // '123.555'
 * toFixed('123.555', 0) // '124'
 * toFixed('123.555', 1) // '123.5'
 * toFixed('123.555', 1, 1) // '123.5'
 * toFixed('123.555', 6) // '123.555000'
 * toFixed('111123.555', 6) // '111123.555000'
 */
function toFixed(s1: string, dp: number, rm = 1) {
  return handleReturn(BN(`${s1}`)['toFixed' as any](dp, rm));
}

/**
 * 带千分位保留几位小数 默认为向下取整
 *
 * @param {string} s1 需要格式化的字符串
 * @param {number} dp 保留几位小数 默认保留所有小数
 * @param {number} [rm=1] 最后一位保留方式，默认为1向下取整
 * @param {Object} format 格式化配置 参考 https://mikemcl.github.io/bignumber.js/#format
 *
 * toFormat('123456789.567') // '123,456,789.567'
 * toFormat('123456789.567', 1) // '123,456,789.5'
 * toFormat('123456789.567', 5) // '123,456,789.56700'
 * toFormat('123456789.567', 1, 1) // '123,456,789.5'
 */
function toFormat(s1, dp?: number, rm = 1, format?: any) {
  return handleReturn(BN(`${s1}`)['toFormat' as any](dp, rm, format));
}

/**
 * 取大的值
 *
 * @param {...str} str 需要取大值的字符串
 * max(['10', '13']) // 13
 * max(['10', '3']) // 10
 * max(['dsaf', 1]) // 0
 *
 */
function max(str) {
  return handleReturn(BN.max(...str));
}

/**
 * 取小的值
 *
 * @param {...str} str 需要取小值的字符串
 * max(['10', '13']) // 10
 * max(['10', '3']) // 3
 * max(['dsaf', 1]) // 0
 *
 */
function min(str) {
  return handleReturn(BN.min(...str));
}

/**
 * 生成固定个数的数字
 *
 * @param {number} len 生成数字的长度
 * @param {number} fill 要生成的数字
 */
function generateNumber(len = 9, fill = 9) {
  return new Array(len).fill(fill).join('');
}

/**
 *
 * 前补0
 *
 * @param {number|string} num 需要补0的数字
 * @param {number} n 总位数，不够在前面补0
 * @return {string} 补0后的字符串
 */
function prefixZero(num, n) {
  return (Array(n).join('0') + num).slice(-n);
  // return String(num).padStart(n, '0')
}

/**
 * 格式化精度，默认值为 `0`
 *
 * @param {string} value 后台配置的精度原始字段，如：0.0001 、0.1 、0
 * @return {number}
 */
function formatPrecision(value) {
  if (typeof value !== 'string' || value.indexOf('.') === -1) return 0;
  const split = value.split('.');
  return split[1] ? split[1].length : 0;
}

/**
 * 格式化数字
 */
function toNumber(value) {
  return Number(value) || 0;
}

function safeToFormat(s1, dp?: number, rm = 1, format?: any) {
  if (s1 == null || s1 == '-1') {
    return '--';
  }
  return handleReturn(BN(`${s1}`)['toFormat' as any](dp, rm, format));
}

function safeToFormat1(s1, dp?: number, rm = 1, format?: any) {
  if (s1 == null || s1 == '-1') {
    return '--';
  }
  if (gt(0.01, s1) && dp == 2) {
    return '<0.01';
  }
  return handleReturn(BN(`${s1}`)['toFormat' as any](dp, rm, format));
}

function formatTrend(num: number) {
  if (num > 0) {
    return `+${safeToFormat(times(num, 100))}%`;
  }
  return `-${safeToFormat(times(num, 100))}%`;
}

function getTrendNum(num: number | string, prefix = 2) {
  const numStr = `${num}`;
  if (
    !numStr.includes('.') ||
    numStr.lastIndexOf('.') + prefix >= numStr.length
  ) {
    return safeToFormat(numStr, prefix);
  }
  const dotIdx = numStr.indexOf('.');
  const dotNum = numStr.slice(dotIdx);
  const rNum = /[1-9]/;
  const dotNumIdx = rNum.exec(dotNum)?.index;
  if (dotNumIdx == null) {
    return safeToFormat(numStr.slice(0, dotIdx + prefix));
  }
  return safeToFormat(numStr.slice(0, dotIdx + dotNumIdx + prefix));
}

function formatTrend1(num: number | string) {
  if (num == '-1' || num == null) {
    return '--';
  }
  if (gt(num, 0)) {
    return `+${getTrendNum(num)}%`;
  }
  const newNum = getTrendNum(num);
  return `${newNum}%`;
}

function maskDigit(num: string, visible: boolean) {
  if (num == null) {
    return '--';
  }
  const len = num.length;
  return visible ? num : `${new Array(len).fill('*').join('')}`;
}

const formatKMB = (num: string, fixScale = 2) => {
  if (num == null || num === '-1') {
    return '--';
  }
  if (lt(num, 1000)) {
    return safeToFormat(num, fixScale);
  } else if (lt(num, 1000000)) {
    const output = safeToFormat(div(num, 1000), fixScale);
    return `${output}K`;
  } else if (lt(num, 1000000000)) {
    const output = safeToFormat(div(num, 1000000), fixScale);
    return `${output}M`;
  } else {
    const output = safeToFormat(div(num, 1000000000), fixScale);
    return `${output}B`;
  }
};

export const formatDexPrice = curryRight(
  formatNumber,
  2
)({
  kmb: false,
  decimal: 4,
  noTrailZero: false,
  roundMode: 7,
});

export {
  plus,
  minus,
  times,
  div,
  idiv,
  sd,
  gt,
  gte,
  lt,
  lte,
  eq,
  toFixed,
  toFormat,
  sum,
  random,
  abs,
  max,
  min,
  generateNumber,
  prefixZero,
  formatPrecision,
  toNumber,
  safeToFormat,
  safeToFormat1,
  maskDigit,
  formatTrend,
  formatTrend1,
  getTrendNum,
  formatKMB,
};
